/**
 * @file Debug preferences that survive a page reload.
 *
 * Dev iterates with vite's watch mode: every save reloads the page, and
 * re-toggling the debug window, the sandbox cheats and the level lab on every
 * refresh gets old fast. This module snapshots all of that live debug state
 * into one localStorage blob and hands it back on boot.
 *
 * It deliberately knows the SHAPE of each state object but owns none of them:
 * the live objects stay in their own modules (sandboxState, levelLab, the
 * debug window, the overlay systems) and this file only copies fields in and
 * out. Gameplay state (the run, score, weapons) is never persisted — this is
 * a developer convenience, not a save system.
 */

export const DEBUG_PREFS_STORAGE_KEY = 'spin:debugPrefs';

/** @returns {Storage | null} localStorage, or null when unavailable/blocked. */
function detectStorage() {
  try {
    const storage = globalThis.localStorage;
    const probe = '__debugPrefsProbe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    // Safari throws instead of returning null when storage is blocked.
    return null;
  }
}

let storage = detectStorage();

/**
 * Replaces the persistence backend (tests).
 * @param {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null} next Store to use.
 * @returns {void}
 */
export function setDebugPrefsStorage(next) {
  storage = next;
}

/**
 * The saved preferences, or null when nothing (or garbage) is stored.
 * @returns {object | null}
 */
export function loadDebugPrefs() {
  if (!storage) return null;
  try {
    const raw = storage.getItem(DEBUG_PREFS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Persists a full snapshot. Call through {@link saveDebugPrefsFrom} unless a
 * test needs to write a hand-built blob.
 * @param {object} prefs Snapshot to store.
 * @returns {boolean} Whether the write landed.
 */
export function saveDebugPrefs(prefs) {
  if (!storage) return false;
  try {
    storage.setItem(DEBUG_PREFS_STORAGE_KEY, JSON.stringify(prefs));
    return true;
  } catch {
    return false;
  }
}

/**
 * Removes every saved debug preference.
 * @returns {void}
 */
export function resetDebugPrefs() {
  try {
    storage?.removeItem(DEBUG_PREFS_STORAGE_KEY);
  } catch {
    // Runtime state is reset by callers; storage failure should not block that.
  }
}

/**
 * Snapshots every live debug surface in one call. Each source is optional so
 * callers can save from wherever they are without importing the others.
 *
 * @param {object} sources Live state objects.
 * @param {object} [sources.sandbox] The sandboxState object.
 * @param {object} [sources.lab] The levelLab object.
 * @param {{ open: boolean, tab: string }} [sources.hub] Debug window state.
 * @param {{ bombers?: boolean, ranged?: boolean }} [sources.overlays] Overlay switches.
 * @param {{ overall: number, components: Record<string, number>, killTimerLabel?: string }} [sources.ui] HUD UI state.
 * @returns {boolean} Whether the write landed.
 */
export function saveDebugPrefsFrom({ sandbox, lab, hub, overlays, ui } = {}) {
  const prefs = loadDebugPrefs() ?? {};
  if (sandbox) {
    prefs.sandbox = {
      enabled: sandbox.enabled,
      terrainMode: sandbox.terrainMode,
      sprayType: sandbox.sprayType,
      enemyToggles: { ...sandbox.enemyToggles },
      cheats: { ...sandbox.cheats }
    };
  }
  if (lab) {
    prefs.lab = {
      theme: lab.theme,
      seed: lab.seed,
      densityMult: lab.densityMult,
      propEnabled: { ...lab.propEnabled }
    };
  }
  if (hub) prefs.hub = { open: hub.open, tab: hub.tab };
  if (overlays) prefs.overlays = { ...prefs.overlays, ...overlays };
  if (ui) {
    prefs.ui = { overall: ui.overall, components: { ...ui.components } };
    if (ui.killTimerLabel !== undefined) prefs.ui.killTimerLabel = ui.killTimerLabel;
  }
  return saveDebugPrefs(prefs);
}

/**
 * Copies saved fields onto a live state object, key by key, ignoring anything
 * the snapshot does not carry — new fields keep their code defaults.
 *
 * @param {object | undefined} saved One section of the stored blob.
 * @param {object} live The live state object to hydrate.
 * @param {string[]} scalarKeys Plain fields to copy when present.
 * @param {string[]} [mapKeys] Record fields to merge key-by-key.
 * @returns {void}
 */
export function hydrate(saved, live, scalarKeys, mapKeys = []) {
  if (!saved) return;
  for (const key of scalarKeys) {
    if (saved[key] !== undefined) live[key] = saved[key];
  }
  for (const key of mapKeys) {
    if (!saved[key] || typeof saved[key] !== 'object') continue;
    for (const inner of Object.keys(live[key])) {
      if (saved[key][inner] !== undefined) live[key][inner] = saved[key][inner];
    }
  }
}
